import { useMemo } from "react";
import { AxiosResponse, InternalAxiosRequestConfig, AxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { useTrace } from "@/context/trace";

interface InternalAxiosRequestConfigWithSpan extends InternalAxiosRequestConfig {
    __sentrySpan?: ReturnType<typeof Sentry.startInactiveSpan>;
}

type TraceInterceptor = {
    onRequest: (
        config: InternalAxiosRequestConfig
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>;
    onResponse: (response: AxiosResponse) => AxiosResponse;
    onResponseError: (error: AxiosError) => Promise<never>;
};

export function useTraceInterceptor(): TraceInterceptor {
    const {
        createTraceId,
        getCurrentTraceId,
        logTraceEvent,
        getCurrentSpanId,
    } = useTrace();

    return useMemo(
        () => ({
            onRequest: (config: InternalAxiosRequestConfig & { __sentrySpan?: ReturnType<typeof Sentry.startInactiveSpan> }) => {
                const sessionId = sessionStorage.getItem("sessionId") || "";

                const span = Sentry.startInactiveSpan({
                    name: `${config.method?.toUpperCase()} ${config.url}`,
                    op: "http.client",
                });

                const spanContext = span.spanContext();
                const traceId = createTraceId();
                const spanId = spanContext.spanId || getCurrentSpanId();

                const traceIdW3CFormat = `00-${traceId
                    .padStart(32, "0")
                    .substring(0, 32)}-${spanId?.padStart(16, "0").substring(0, 16)}-01`;

                config.headers = config.headers || {};
                config.headers["x-trace-id"] = traceIdW3CFormat;
                config.headers["x-session-id"] = sessionId;

                const traceIdFromHeader = traceIdW3CFormat.split("-")[1];
                Sentry.setTag("apiTraceId", traceIdFromHeader);
                Sentry.setTag("sessionId", sessionId);
                Sentry.setContext("request", {
                    url: config.url,
                    method: config.method,
                    traceId,
                    spanId,
                    sessionId,
                });

                logTraceEvent({
                    traceId,
                    event: "api_request",
                    level: "info",
                    message: `${config.method?.toUpperCase()} ${config.url}`,
                    metadata: {
                        method: config.method,
                        url: config.url,
                        spanId,
                    },
                });

                (config).__sentrySpan = span;

                return config;
            },

            onResponse: (response: AxiosResponse) => {
                const config = response.config as InternalAxiosRequestConfigWithSpan;
                const span = config?.__sentrySpan;

                if (span) {
                    span.setStatus({ code: 1, message: "ok" });
                    span.end();
                    delete config.__sentrySpan;
                }

                const traceId =
                    response.config.headers?.["x-trace-id"] ||
                    response.config.headers?.["X-Trace-Id"];
                logTraceEvent({
                    traceId,
                    event: "api_response",
                    level: "info",
                    message: `${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
                    metadata: {
                        status: response.status,
                        method: response.config.method,
                        url: response.config.url,
                    },
                });
                return response;
            },

            onResponseError: (error: AxiosError) => {
                const config = error.config as InternalAxiosRequestConfigWithSpan;
                const span = config?.__sentrySpan;

                if (span) {
                    span.setStatus({ code: 2, message: "error" });
                    span.end();
                    delete config.__sentrySpan;
                }
                return Promise.reject(error);
            },
        }),
        [createTraceId, getCurrentTraceId, logTraceEvent, getCurrentSpanId]
    );
}

